import React, { useState } from 'react'
import PropTypes from 'prop-types'
import MarkdownRenderer from 'components/MarkdownRenderer'
import Link from 'components/Link'
import GA4 from 'utils/GA4'
import { useTheme } from 'styled-components'
import {
  Wrapper,
  HeadingBenefits,
  HeadingWrapper,
  HeadingDescription,
  AllLevelsWrapper,
  BenefitsWrapper,
  SingleLevelWrapper,
  LevelHeadline,
  LevelBandText,
  LevelDot,
  SingleBenefitWrapper,
  BenefitTitle,
  DotsBox,
  BenefitDot,
  TACText,
} from './MembersBenefits.style'

const MembersBenefits = (props) => {
  const {
    headline,
    description,
    level1Headline,
    level2Headline,
    level3Headline,
    level1Colour,
    level2Colour,
    level3Colour,
    level1BandText,
    level2BandText,
    level3BandText,
    level1Benefits,
    level2Benefits,
    level3Benefits,
    level2DisplayPreviousLevels,
    level3DisplayPreviousLevels,
    termsAndConditions,
    termsAndConditionsUrl,
    villageSlug,
  } = props
  const { colors } = useTheme()
  const levelColors = [
    level1Colour || colors.level1Colour,
    level2Colour || colors.level2Colour,
    level3Colour || colors.level3Colour,
  ]

  const [activeLevel, setactiveLevel] = useState(1)
  const [contentColor, setContentColor] = useState(levelColors[0])

  const handleLevelClick = (levelClicked, levelItemHeadline) => {
    GA4('member_benefits_tab_click', {
      headline: levelItemHeadline,
      village_name: villageSlug,
    })
    setactiveLevel(levelClicked)
    setContentColor(levelColors[levelClicked - 1])
  }

  return (
    <>
      <Wrapper>
        <HeadingWrapper>
          <HeadingBenefits color={colors.sagemediumToBlack}>
            {headline}
          </HeadingBenefits>
          <HeadingDescription as={MarkdownRenderer} html={description} />
        </HeadingWrapper>
        <AllLevelsWrapper>
          <SingleLevelWrapper
            onClick={() => handleLevelClick(1, level1Headline)}
            isActive={activeLevel === 1}
            color={contentColor}>
            <LevelHeadline isActive={activeLevel === 1} color={contentColor}>
              {level1Headline}
            </LevelHeadline>
            <LevelBandText isActive={activeLevel === 1}>
              &zwj;{level1BandText}
            </LevelBandText>
            <LevelDot isActive={activeLevel === 1} color={contentColor}>
              &#8226;
            </LevelDot>
          </SingleLevelWrapper>
          <SingleLevelWrapper
            onClick={() => handleLevelClick(2, level2Headline)}
            isActive={activeLevel === 2}
            color={contentColor}>
            <LevelHeadline isActive={activeLevel === 2} color={contentColor}>
              {level2Headline}
            </LevelHeadline>
            <LevelBandText isActive={activeLevel === 2}>
              &zwj;{level2BandText}
            </LevelBandText>
            <LevelDot isActive={activeLevel === 2} color={contentColor}>
              &#8226;
            </LevelDot>
          </SingleLevelWrapper>
          <SingleLevelWrapper
            onClick={() => handleLevelClick(3, level3Headline)}
            isActive={activeLevel === 3}
            color={contentColor}>
            <LevelHeadline isActive={activeLevel === 3} color={contentColor}>
              {level3Headline}
            </LevelHeadline>
            <LevelBandText isActive={activeLevel === 3}>
              &zwj;{level3BandText}
            </LevelBandText>
            <LevelDot isActive={activeLevel === 3} color={contentColor}>
              &#8226;
            </LevelDot>
          </SingleLevelWrapper>
        </AllLevelsWrapper>
        <BenefitsWrapper>
          {level1Benefits.map((benefit) => {
            return (
              <SingleBenefitWrapper>
                <BenefitTitle>{benefit.title}</BenefitTitle>
                <DotsBox>
                  <BenefitDot
                    isActive={activeLevel === 1}
                    color={levelColors[0]}>
                    &#8226;
                  </BenefitDot>
                  <BenefitDot
                    isActive={activeLevel === 2 && level2DisplayPreviousLevels}
                    color={levelColors[1]}>
                    &#8226;
                  </BenefitDot>
                  <BenefitDot
                    isActive={activeLevel === 3 && level3DisplayPreviousLevels}
                    color={levelColors[2]}>
                    &#8226;
                  </BenefitDot>
                </DotsBox>
              </SingleBenefitWrapper>
            )
          })}
          {level2Benefits.map((benefit) => {
            return (
              <SingleBenefitWrapper>
                <BenefitTitle>{benefit.title}</BenefitTitle>
                <DotsBox>
                  <BenefitDot
                    isActive={activeLevel === 2}
                    color={levelColors[1]}>
                    &#8226;
                  </BenefitDot>
                  <BenefitDot
                    isActive={activeLevel === 3 && level3DisplayPreviousLevels}
                    color={levelColors[2]}>
                    &#8226;
                  </BenefitDot>
                </DotsBox>
              </SingleBenefitWrapper>
            )
          })}
          {level3Benefits.map((benefit) => {
            return (
              <SingleBenefitWrapper>
                <BenefitTitle>{benefit.title}</BenefitTitle>
                <DotsBox>
                  <BenefitDot
                    isActive={activeLevel === 3}
                    color={levelColors[2]}>
                    &#8226;
                  </BenefitDot>
                </DotsBox>
              </SingleBenefitWrapper>
            )
          })}
        </BenefitsWrapper>
        <Link to={termsAndConditionsUrl}>
          <TACText>{termsAndConditions}</TACText>
        </Link>
      </Wrapper>
    </>
  )
}

MembersBenefits.propTypes = {
  headline: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  level1Headline: PropTypes.string.isRequired,
  level2Headline: PropTypes.string.isRequired,
  level3Headline: PropTypes.string.isRequired,
  level1Colour: PropTypes.string.isRequired,
  level2Colour: PropTypes.string.isRequired,
  level3Colour: PropTypes.string.isRequired,
  level1Benefits: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.array]))
    .isRequired,
  level2Benefits: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.array]))
    .isRequired,
  level3Benefits: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.array]))
    .isRequired,
  level2DisplayPreviousLevels: PropTypes.bool.isRequired,
  level3DisplayPreviousLevels: PropTypes.bool.isRequired,
  level1BandText: PropTypes.string.isRequired,
  level2BandText: PropTypes.string.isRequired,
  level3BandText: PropTypes.string.isRequired,
  termsAndConditions: PropTypes.string.isRequired,
  termsAndConditionsUrl: PropTypes.string.isRequired,
  villageSlug: PropTypes.string,
}

MembersBenefits.defaultProps = {
  villageSlug: '',
}

export default MembersBenefits
